"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Calendar from "../../assets/calendar.png";
import NotificationDetailsModal from "../notifications/NotificationDetailsModal";
import NotificationTypeIcon from "../notifications/NotificationTypeIcon";
import { getNotificationActionLabel, getNotificationActionUrl } from "../../utils/notificationNavigation";
import profilePlaceholder from "../../assets/profile-placeholder.svg";

const Avatar = ({ initials, image, size = "small" }) => {
  const sizeClasses =
    size === "small" ? "w-10 h-10 text-xs" : "w-10 h-10 text-sm";

  return (
    <div
      className={`${sizeClasses} rounded-full bg-gray-300 text-white flex items-center justify-center font-medium border-2 border-white -mr-2 overflow-hidden`}
    >
      {image ? (
        <img
          src={image || profilePlaceholder}
          alt={initials}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        />
      ) : null}
      <div
        className={`w-full h-full flex items-center justify-center ${
          image ? "hidden" : "flex"
        }`}
        style={{ display: image ? "none" : "flex" }}
      >
        {initials}
      </div>
    </div>
  );
};

function Card({
  title = "Dashboard",
  birthdayCelebrants = [],
  birthdayCelebrantsTotal,
  workAnniversaries = [],
  workAnniversariesTotal,
  holidays = [],
  notifications = [],
  recentRequests = [],
  onNotificationDeleted,
}) {
  const currentMonthName = new Date().toLocaleString("en-US", {
    month: "long",
  });
  const birthdayCount = Number(
    birthdayCelebrantsTotal ?? birthdayCelebrants.length ?? 0,
  );
  const anniversaryCount = Number(
    workAnniversariesTotal ?? workAnniversaries.length ?? 0,
  );
  const [currentHolidayIndex, setCurrentHolidayIndex] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const nextHoliday = () => {
    if (holidays.length < 2) {
      return;
    }

    setCurrentHolidayIndex((prev) => (prev + 1) % holidays.length);
  };

  const prevHoliday = () => {
    if (holidays.length < 2) {
      return;
    }

    setCurrentHolidayIndex(
      (prev) => (prev - 1 + holidays.length) % holidays.length,
    );
  };

  const visibleHolidays = holidays.length
    ? [0, 1]
        .slice(0, Math.min(2, holidays.length))
        .map((offset) => holidays[(currentHolidayIndex + offset) % holidays.length])
    : [];

  return (
    <div className="lg:p-6 font-Nunito">
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg mb-6">
              <div className="p-4 flex justify-between items-center border-b border-gray-100">
                <div className="flex items-center">
                  <div className="flex mr-3">
                    {birthdayCelebrants.slice(0, 3).map((person, index) => (
                      <Avatar
                        key={index}
                        initials={person.initials}
                        image={person.image}
                      />
                    ))}
                    {birthdayCount > 3 && (
                      <div className="w-8 h-8 rounded-full bg-[#1234CD] text-white flex items-center justify-center font-medium text-xs border-2 border-white -mr-2">
                        +{birthdayCount - 3}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold mb-0">
                      {currentMonthName} birthday celebrants
                    </h3>

                    <Link
                      className="text-[#0811D4] font-semibold text-xs hover:text-blue-800"
                      to="/company-settings/staff-birthdays"
                    >
                      Click to see more -&gt;
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg mb-6">
              <div className="p-4 flex justify-between items-center border-b border-gray-100">
                <div className="flex items-center">
                  <div className="flex mr-3">
                    {workAnniversaries.slice(0, 3).map((person, index) => (
                      <Avatar
                        key={index}
                        initials={person.initials}
                        image={person.image}
                      />
                    ))}
                    {anniversaryCount > 3 && (
                      <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-medium text-xs border-2 border-white -mr-2">
                        +{anniversaryCount - 3}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold mb-0">
                      {currentMonthName} work anniversaries
                    </h3>
                    <Link
                      to="/company-settings/work-anniversaries"
                      className="text-[#0811D4] font-semibold text-xs hover:text-blue-800"
                    >
                      Click to see more -&gt;
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="p-4 flex items-center justify-between border-b border-gray-100">
                <button
                  type="button"
                  onClick={prevHoliday}
                  disabled={holidays.length < 2}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Previous holiday"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                <h3 className="text-sm font-semibold text-gray-900">
                  UPCOMING HOLIDAY
                </h3>

                <button
                  type="button"
                  onClick={nextHoliday}
                  disabled={holidays.length < 2}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Next holiday"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4">
                <div className="flex rounded-xl bg-[#EFF7FC] items-center divide-x">
                  {visibleHolidays.length > 0 ? (
                    visibleHolidays.map((holiday, index) => (
                      <div
                        key={`${holiday.id || holiday.name}-${index}`}
                        className="flex w-full items-center gap-3 px-4 py-3"
                      >
                        <div className="bg-blue-100 p-2 rounded-md">
                          <img
                            src={Calendar}
                            className="h-5 w-5 object-contain"
                            alt="calendar"
                          />
                        </div>

                        <div>
                          <div className="font-medium text-sm text-gray-900">
                            {holiday.flag ? `${holiday.flag} ` : ""}
                            {holiday.date}
                          </div>
                          <div className="text-xs text-gray-600">
                            {holiday.name}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="w-full px-4 py-5 text-sm text-gray-500">
                      No upcoming holidays yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 ">
              <h2 className="text-lg text-center font-bold m-0">
                Recent Notifications
              </h2>
            </div>
            <div className="p-4">
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                <div
                  role="button"
                  tabIndex={0}
                  key={notification.id}
                  onClick={() => setSelectedNotification(notification)}
                  onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedNotification(notification); } }}
                  className="mb-2 flex w-full items-start border border-gray-100 py-3 text-left transition-colors hover:bg-slate-50 last:border-b-0"
                >
                  <NotificationTypeIcon notification={notification} />
                  <div className="flex-1 mx-3">
                    <p className="text-base font-medium text-[#272525] mb-1">
                      {notification.message}
                    </p>
                    <span className="text-xs text-gray-600">
                      {notification.time}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getNotificationActionUrl(notification) ? <button type="button" onClick={(event) => { event.stopPropagation(); window.location.href = getNotificationActionUrl(notification); }} className="rounded-md border border-[#1234CD] px-2 py-1 text-xs font-semibold text-[#1234CD] hover:bg-blue-50">{getNotificationActionLabel(notification)}</button> : null}
                    <div
                      className={`w-2 h-2 rounded-full mr-2 ${
                        notification.status === "approved"
                          ? "bg-green-500"
                          : notification.status === "pending"
                            ? "bg-yellow-500"
                            : "bg-red-500"
                      }`}
                    ></div>
                    <span className="text-base text-gray-600">
                      ...
                    </span>
                  </div>
                </div>
                ))
              ) : (
                <p className="py-6 text-center text-sm text-gray-500">
                  No notifications yet.
                </p>
              )}
              <Link
                to="/notification"
                className="mt-3 inline-flex w-full items-center justify-center rounded-xl border border-[#1234CD] px-4 py-2 text-sm font-semibold text-[#1234CD] transition-colors hover:bg-blue-50"
              >
                View more
              </Link>
            </div>
          </div>

          <NotificationDetailsModal
            notification={selectedNotification}
            onClose={() => setSelectedNotification(null)}
            onDelete={onNotificationDeleted}
          />

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="p-4 ">
              <h2 className="text-lg font-semibold m-0">Recent Requests</h2>
            </div>
            <div>
              {recentRequests.length > 0 ? (
                recentRequests.map((request) => (
                  <Link
                    key={request.id}
                    to={`/requests/request-details?applicationId=${encodeURIComponent(request.id)}`}
                    className="flex items-start py-3 border-gray-100 rounded-lg transition-colors hover:bg-[#F8FAFF]"
                  >
                    <Avatar
                      initials={request.initials}
                      image={request.image}
                    />
                    <div className="flex-1 mx-3">
                      <p className="text-sm font-medium text-[#272525] mb-1">
                        {request.requestName}
                      </p>
                      <p className="text-xs text-gray-600 mb-1">
                        {request.applicantName}
                      </p>
                      <span className="text-xs text-gray-600">
                        {request.time}
                      </span>
                    </div>
                    <span
                      className={`mt-1 rounded-full px-2 py-1 text-[11px] font-semibold ${
                        request.status === "Approved"
                          ? "bg-green-100 text-green-700"
                          : request.status === "Declined"
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {request.status}
                    </span>
                  </Link>
                ))
              ) : (
                <p className="py-6 text-center text-sm text-gray-500">
                  No recent requests yet.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Card;
